import { _js } from '@ifixit/utils/localize';
import { createContext, Dispatch, SetStateAction, useState } from 'react';
import type { ComponentType } from 'react';
import { requireAuth } from 'Shared/require-auth';
import LoadingIndicator from 'Shared/loading_indicator';
import ApiRequest from 'Shared/api_2_0';
import AjaxIO from 'Shared/utils';

const noop = () => {};

const defaultContext = {
   isSubscribed: false,
   subscribe: noop as (_guideid: number, _langid: string) => void,
   unsubscribe: noop as (_guideid: number, _langid: string) => void,
};

const GuideSubscriptionContext = createContext(defaultContext);

function withGuideSubscription<T>(Component: ComponentType<T>) {
   return function GuideSubscriptionProvider(props: { isSubscribed?: boolean } & T) {
      const [isSubscribed, setSubscribed] = useState(
         props.isSubscribed || defaultContext.isSubscribed
      );

      const guideSubscriptionContext = {
         subscribe: (guideid: number, langid: string) => {
            doSubscriptionGuide(guideid, langid, true, setSubscribed);
         },
         unsubscribe: (guideid: number, langid: string) => {
            doSubscriptionGuide(guideid, langid, false, setSubscribed);
         },
         isSubscribed,
      };

      return (
         <GuideSubscriptionContext.Provider value={guideSubscriptionContext}>
            <Component {...props} />
         </GuideSubscriptionContext.Provider>
      );
   };
}

function doSubscriptionGuide(
   guideid: number,
   langid: string,
   wantsToSubscribe: boolean,
   setSubscribed: Dispatch<SetStateAction<boolean>>
) {
   requireAuth({
      from: '',
      message: _js('Log in to subscribe.'),
      onAuthorize: LoadingIndicator.withEventHandler(
         () => {
            const subPromise = getSubPromise(guideid, langid, wantsToSubscribe);
            return subPromise.then(() => {
               setSubscribed(wantsToSubscribe);
            });
         },
         {
            pendingMessage: wantsToSubscribe
               ? _js('Adding Subscription')
               : _js('Removing Subscription'),
            successMessage: wantsToSubscribe
               ? _js('Added Subscription')
               : _js('Removed Subscription'),
            failureMessage: _js('Failure'),
         }
      ),
   });
}

function handleLegacyWatchHtml(wantsToSubscribe: boolean) {
   const on = document.getElementById('commentsWatchOn');
   const off = document.getElementById('commentsWatchOff');
   if (!on || !off) {
      return;
   }

   on.style.display = wantsToSubscribe ? 'none' : 'inline';
   off.style.display = wantsToSubscribe ? 'inline' : 'none';
}

function getSubPromise(
   guideid: number,
   langid: string,
   wantsToSubscribe: boolean
): Promise<[void, void]> {
   const guide = window.guide_constants.DocumentWatch('DOCTYPE_GUIDE');
   const edit = window.guide_constants.DocumentWatch('LEVEL_EDIT');
   const editSubUrl = `subscribe/${guide}/${guideid}/${langid}/${edit}`;
   const method = wantsToSubscribe ? 'put' : 'delete';
   const editSubscriptionRequest = new ApiRequest(editSubUrl, { method });

   const commentsSubState = wantsToSubscribe ? 'on' : 'off';
   const commentSubscriptionRequest = new AjaxIO('watch');

   return Promise.all([
      editSubscriptionRequest.send() as Promise<void>,
      commentSubscriptionRequest
         .send(commentsSubState, 'guide', guideid.toString())
         .then(() => handleLegacyWatchHtml(wantsToSubscribe)) as Promise<void>,
   ]);
}

export { GuideSubscriptionContext, withGuideSubscription };
